import {
  type GraphQLSupporterContact,
  type GraphQLVolunteerEvent,
  type GraphQLVolunteerSlot,
  GraphQLVolunteerStatus,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  VolunteerSlotStatusEnum,
  type VolunteerSlotStatusUpdateRequest,
} from '@sola/schema/dotcom/supporters/volunteers.ts';

// ── Volunteer slot status helpers ────────────────────────────────────────────

/**
 * Returns true when the volunteer slot is in a state that still allows cancellation.
 */
export const isVolunteerSlotCancellable = (
  status?: GraphQLVolunteerStatus | null,
): boolean =>
  status !== GraphQLVolunteerStatus.Canceled &&
  status !== GraphQLVolunteerStatus.Completed &&
  status !== GraphQLVolunteerStatus.CompletedNoShow &&
  status !== GraphQLVolunteerStatus.Declined;

/**
 * Maps every raw upstream volunteer status to the status exposed by GraphQL.
 */
export const rawVolunteerStatusMap: Readonly<
  Record<VolunteerSlotStatusEnum, GraphQLVolunteerStatus>
> = {
  [VolunteerSlotStatusEnum.Available]: GraphQLVolunteerStatus.Available,
  [VolunteerSlotStatusEnum.Approved]: GraphQLVolunteerStatus.Approved,
  [VolunteerSlotStatusEnum.PendingApproval]: GraphQLVolunteerStatus.Pending,
  [VolunteerSlotStatusEnum.PendingApprovalFirst]:
    GraphQLVolunteerStatus.Pending,
  [VolunteerSlotStatusEnum.PendingApprovalGuest]:
    GraphQLVolunteerStatus.Pending,
  [VolunteerSlotStatusEnum.CanceledAbandoned]: GraphQLVolunteerStatus.Canceled,
  [VolunteerSlotStatusEnum.CanceledByVolunteer]:
    GraphQLVolunteerStatus.Canceled,
  [VolunteerSlotStatusEnum.CanceledCOVIDConcern]:
    GraphQLVolunteerStatus.Canceled,
  [VolunteerSlotStatusEnum.CanceledEvent]: GraphQLVolunteerStatus.Canceled,
  [VolunteerSlotStatusEnum.CanceledWithinFortyEightHours]:
    GraphQLVolunteerStatus.Canceled,
  [VolunteerSlotStatusEnum.Completed]: GraphQLVolunteerStatus.Completed,
  [VolunteerSlotStatusEnum.Confirmed]: GraphQLVolunteerStatus.Confirmed,
  [VolunteerSlotStatusEnum.CompletedNoShow]:
    GraphQLVolunteerStatus.CompletedNoShow,
  [VolunteerSlotStatusEnum.Declined]: GraphQLVolunteerStatus.Declined,
  [VolunteerSlotStatusEnum.Waitlist]: GraphQLVolunteerStatus.Waitlist,
};

const caseInsensitiveStatusLookup: Readonly<
  Record<string, GraphQLVolunteerStatus>
> = Object.fromEntries(
  Object.entries(rawVolunteerStatusMap).map(([rawStatus, status]) => [
    rawStatus.toLowerCase(),
    status,
  ]),
);

/**
 * Normalize a raw upstream volunteer status into its GraphQL representation.
 */
export const normalizeVolunteerStatus = (
  rawStatus: string | null | undefined,
): GraphQLVolunteerStatus | null => {
  const normalizedRawStatus = rawStatus?.trim();

  if (!normalizedRawStatus) {
    return null;
  }

  return (
    rawVolunteerStatusMap[normalizedRawStatus as VolunteerSlotStatusEnum] ??
    caseInsensitiveStatusLookup[normalizedRawStatus.toLowerCase()] ??
    null
  );
};

// ── Volunteer slot grouping & sorting ────────────────────────────────────────

export type VolunteerSlotGroup = Pick<
  GraphQLVolunteerSlot,
  'endDate' | 'startDate' | 'status'
> & {
  contactIds: string[];
  slots: GraphQLVolunteerSlot[];
  title: string;
};

export interface GroupVolunteerSlotsOptions {
  /** When true, slots are also partitioned by status. Defaults to true. */
  groupByStatus?: boolean;
}

/**
 * Comparator ordering by start time, breaking ties on end time. `startDate`
 * and `endDate` are ISO date strings.
 */
const byStartThenEnd = (
  a: { startDate: string; endDate: string },
  b: { startDate: string; endDate: string },
): number => {
  const startDiff = Date.parse(a.startDate) - Date.parse(b.startDate);
  return startDiff === 0
    ? Date.parse(a.endDate) - Date.parse(b.endDate)
    : startDiff;
};

/**
 * Combine equivalent volunteer slots into the data needed for one slot card.
 *
 * Slots are equivalent when their displayed title, start time, and end time
 * match. By default status is also included in the grouping key — pass
 * `{ groupByStatus: false }` to ignore status when grouping.
 *
 * Contact IDs are deduplicated in their original order.
 *
 * Groups are returned sorted by start time (then end time), so consumers
 * render shifts chronologically regardless of the order slots arrive from the
 * API.
 */
export const groupVolunteerSlots = (
  slots: GraphQLVolunteerSlot[],
  fallbackTitle: string,
  { groupByStatus = true }: GroupVolunteerSlotsOptions = {},
): VolunteerSlotGroup[] => {
  const groups = new Map<string, VolunteerSlotGroup>();

  for (const slot of slots) {
    const { shiftName, startDate, endDate, status, contactId } = slot;
    const title = shiftName ?? fallbackTitle;
    const key = groupByStatus
      ? JSON.stringify([title, startDate, endDate, status ?? null])
      : JSON.stringify([title, startDate, endDate]);
    const existingGroup = groups.get(key);

    if (existingGroup) {
      existingGroup.slots.push(slot);
      if (!existingGroup.contactIds.includes(contactId)) {
        existingGroup.contactIds.push(contactId);
      }
      continue;
    }

    groups.set(key, {
      contactIds: [contactId],
      endDate,
      slots: [slot],
      startDate,
      status,
      title,
    });
  }

  return [...groups.values()].sort(byStartThenEnd);
};

export const createVolunteerSlotStatusUpdates = (
  slots: GraphQLVolunteerSlot[],
  status: VolunteerSlotStatusEnum,
): VolunteerSlotStatusUpdateRequest[] =>
  slots.map(({ contactId, id: slotId }) => ({ contactId, slotId, status }));

export interface VolunteerSlotContact {
  email: string | undefined;
  id: string;
  name: string;
}

// ── Contact types ────────────────────────────────────────────────────────────

export type KnownContact = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  email: string | null;
  phone: string | null;
};

export type GuestAgeRange = '14-15' | '16-17' | '18+';

export type GuestRelationship =
  | 'child'
  | 'nonresident-child'
  | 'spouse'
  | 'friend'
  | 'relative'
  | 'other';

/** A guest added during registration (not a persisted supporter contact). */
export interface GuestContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  ageRange: GuestAgeRange;
  relationship: GuestRelationship;
}

/** Either a known supporter contact or a registration-time guest. */
export type VolunteerContact = KnownContact | GuestContact;

export const getVolunteerSlotContacts = (
  contactIds: string[],
  contacts: GraphQLSupporterContact[],
): VolunteerSlotContact[] =>
  [...new Set(contactIds)].flatMap((contactId) => {
    const contact = contacts.find(({ id }) => id === contactId);

    if (!contact) {
      return [];
    }

    const name =
      [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
      contact.email?.trim();

    return name
      ? [{ email: contact.email?.trim() || undefined, id: contact.id, name }]
      : [];
  });

const getVolunteerEventShiftStartTime = (
  event: GraphQLVolunteerEvent,
): number =>
  event.slots.length > 0
    ? Math.min(...event.slots.map(({ startDate }) => Date.parse(startDate)))
    : Date.parse(event.startDate);

export const sortVolunteerEvents = (
  events: GraphQLVolunteerEvent[],
): GraphQLVolunteerEvent[] =>
  events.toSorted(
    (firstEvent, secondEvent) =>
      getVolunteerEventShiftStartTime(firstEvent) -
      getVolunteerEventShiftStartTime(secondEvent),
  );

// ── Shared view-model types ──────────────────────────────────────────────────

/** Slot group with contacts resolved (contactIds replaced by VolunteerSlotContact[]). */
export type ResolvedSlotGroup = Omit<VolunteerSlotGroup, 'contactIds'> & {
  contacts: VolunteerSlotContact[];
};

/**
 * Non-canceled slots for an event.
 */
export const getActiveVolunteerSlots = (
  slots: GraphQLVolunteerSlot[],
): GraphQLVolunteerSlot[] =>
  slots.filter(({ status }) => status !== GraphQLVolunteerStatus.Canceled);

/** Base view-model for a volunteer event with resolved slot groups. */
export type BaseVolunteerEventViewModel = GraphQLVolunteerEvent & {
  contacts: VolunteerSlotContact[];
  slotGroups: ResolvedSlotGroup[];
};

/**
 * Confirmation-flow view-model: adds non-canceled `activeSlots` on top of the
 * base view-model. Used by the confirmation page and registration sheet, where
 * `contacts` are derived from active slots.
 */
export type ConfirmationVolunteerEventViewModel =
  BaseVolunteerEventViewModel & {
    activeSlots: GraphQLVolunteerSlot[];
  };

/**
 * Build the confirmation view-model for a volunteer event: non-canceled
 * `activeSlots`, `contacts`, and `slotGroups` with contactIds resolved.
 * Shared by the confirmation page and registration sheet so both match.
 */
export const buildVolunteerEventViewModel = (
  event: GraphQLVolunteerEvent,
  contacts: GraphQLSupporterContact[],
): ConfirmationVolunteerEventViewModel => {
  const { locationName, slots } = event;

  const activeSlots = getActiveVolunteerSlots(slots);

  return {
    ...event,
    activeSlots,
    contacts: getVolunteerSlotContacts(
      activeSlots.map(({ contactId }) => contactId),
      contacts,
    ),
    slotGroups: groupVolunteerSlots(slots, locationName, {
      groupByStatus: false,
    }).map(({ contactIds, ...slotGroup }) => ({
      ...slotGroup,
      contacts: getVolunteerSlotContacts(contactIds, contacts),
    })),
  };
};
